import type { HeaderClock, SidFile, SidHeader, SidModel } from './types';

export class SidFileError extends Error {}

const CLOCKS: HeaderClock[] = ['unknown', 'PAL', 'NTSC', 'both'];
const MODELS: SidModel[] = ['unknown', '6581', '8580', 'both'];

function u16(b: Uint8Array, o: number): number {
  return (b[o] << 8) | b[o + 1];
}

function u32(b: Uint8Array, o: number): number {
  return ((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0;
}

/** Header strings are Windows-1252 / Latin-1, zero padded to 32 bytes. */
function str(b: Uint8Array, o: number): string {
  let s = '';
  for (let i = 0; i < 32; i++) {
    const c = b[o + i];
    if (c === 0) break;
    s += String.fromCharCode(c);
  }
  return s.trim();
}

/** Decodes an extra-SID address byte ($Dxx0) – returns 0 if absent/invalid. */
function sidAddress(v: number): number {
  const valid = (v >= 0x42 && v <= 0x7f) || (v >= 0xe0 && v <= 0xfe);
  return valid && (v & 1) === 0 ? 0xd000 | (v << 4) : 0;
}

export function parseSid(bytes: Uint8Array): SidFile {
  if (bytes.length < 0x76) throw new SidFileError('This file is too small to be a SID tune.');
  const magic = String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3]);
  if (magic !== 'PSID' && magic !== 'RSID') {
    throw new SidFileError('This is not a SID file (expected a PSID or RSID header).');
  }
  const version = u16(bytes, 4);
  if (version < 1 || version > 4) throw new SidFileError(`Unsupported SID header version ${version}.`);
  if (magic === 'RSID' && version < 2)
    throw new SidFileError('Invalid RSID file (version must be 2 or higher).');

  const dataOffset = u16(bytes, 6);
  if (dataOffset !== 0x76 && dataOffset !== 0x7c) {
    throw new SidFileError(`Unexpected data offset $${dataOffset.toString(16)} in SID header.`);
  }
  if (bytes.length <= dataOffset) throw new SidFileError('The SID file contains no music data.');

  let loadAddress = u16(bytes, 8);
  let dataStart = dataOffset;
  if (loadAddress === 0) {
    if (bytes.length < dataOffset + 2) throw new SidFileError('The SID file is truncated.');
    loadAddress = bytes[dataOffset] | (bytes[dataOffset + 1] << 8);
    dataStart += 2;
  }
  const data = bytes.slice(dataStart);
  if (loadAddress + data.length > 0x10000) {
    throw new SidFileError('The music data does not fit into C64 memory.');
  }

  let initAddress = u16(bytes, 0x0a);
  const playAddress = u16(bytes, 0x0c);
  const songs = Math.max(1, u16(bytes, 0x0e));
  let startSong = u16(bytes, 0x10);
  if (startSong < 1 || startSong > songs) startSong = 1;
  const speed = u32(bytes, 0x12);

  let flags = 0;
  let secondSidAddress = 0;
  let thirdSidAddress = 0;
  if (version >= 2 && dataOffset >= 0x7c) {
    flags = u16(bytes, 0x76);
    if (version >= 3) secondSidAddress = sidAddress(bytes[0x7a]);
    if (version >= 4) thirdSidAddress = sidAddress(bytes[0x7b]);
  }
  if (magic === 'PSID' && initAddress === 0) initAddress = loadAddress;

  const header: SidHeader = {
    format: magic,
    version,
    dataOffset,
    loadAddress,
    initAddress,
    playAddress,
    songs,
    startSong,
    speed,
    name: str(bytes, 0x16),
    author: str(bytes, 0x36),
    released: str(bytes, 0x56),
    flags,
    musPlayer: (flags & 1) !== 0,
    psidSpecificOrBasic: (flags & 2) !== 0,
    clock: CLOCKS[(flags >> 2) & 3],
    sidModel: MODELS[(flags >> 4) & 3],
    secondSidAddress,
    thirdSidAddress,
    sidCount: 1 + (secondSidAddress ? 1 : 0) + (thirdSidAddress ? 1 : 0),
    dataLength: data.length,
  };
  return { header, data };
}

/** Returns true if the given (1-based) subtune uses CIA timing rather than the vertical blank. */
export function subtuneUsesCia(header: SidHeader, subtune: number): boolean {
  if (header.format === 'RSID') return false;
  const bit = Math.min(subtune, 32) - 1;
  return ((header.speed >>> bit) & 1) === 1;
}

/** Builds a PSID v2 file – used for tests and the built-in demo tune. */
export function buildPsid(opts: {
  name: string;
  author: string;
  released: string;
  loadAddress: number;
  initAddress: number;
  playAddress: number;
  data: Uint8Array;
  songs?: number;
  startSong?: number;
  speed?: number;
  flags?: number;
  format?: 'PSID' | 'RSID';
}): Uint8Array {
  const out = new Uint8Array(0x7c + 2 + opts.data.length);
  const w16 = (o: number, v: number) => {
    out[o] = (v >> 8) & 0xff;
    out[o + 1] = v & 0xff;
  };
  const wstr = (o: number, s: string) => {
    for (let i = 0; i < Math.min(32, s.length); i++) out[o + i] = s.charCodeAt(i) & 0xff;
  };
  const fmt = opts.format ?? 'PSID';
  for (let i = 0; i < 4; i++) out[i] = fmt.charCodeAt(i);
  w16(4, 2);
  w16(6, 0x7c);
  w16(8, 0); // load address taken from data
  w16(0x0a, opts.initAddress);
  w16(0x0c, opts.playAddress);
  w16(0x0e, opts.songs ?? 1);
  w16(0x10, opts.startSong ?? 1);
  const speed = opts.speed ?? 0;
  out[0x12] = (speed >>> 24) & 0xff;
  out[0x13] = (speed >>> 16) & 0xff;
  out[0x14] = (speed >>> 8) & 0xff;
  out[0x15] = speed & 0xff;
  wstr(0x16, opts.name);
  wstr(0x36, opts.author);
  wstr(0x56, opts.released);
  w16(0x76, opts.flags ?? 0x14); // PAL, 6581
  out[0x7c] = opts.loadAddress & 0xff;
  out[0x7d] = opts.loadAddress >> 8;
  out.set(opts.data, 0x7e);
  return out;
}
